// src/components/PrivacyPolicy.js

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 px-6 md:px-24 py-16 text-gray-800">
      <h1 className="text-4xl font-bold mb-6 text-center">Privacy Policy</h1>
      <p className="text-center text-gray-600 mb-12">
        Last updated: November 2025
      </p>

      <div className="space-y-8 text-[17px] leading-relaxed">

        <p>
          Thank you for choosing <strong>Smarty</strong>, a product of{" "}
          <strong>Accennovate Tech Solutions</strong>. We value your trust and are committed to protecting your privacy.
          This Privacy Policy explains how we collect, use, store, and safeguard your information when you use our
          services. By using Smarty, you agree to this policy.
        </p>

        {/* Section 1 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">1. Information We Collect</h2>

          <h3 className="text-xl font-medium mt-4 mb-2">1.1 Information You Provide</h3>
          <ul className="list-disc ml-6 space-y-1">
            <li>Name, email, phone number, or profile details</li>
            <li>Messages, questions, uploaded files, and activity inside Smarty</li>
            <li>Billing name, address, ZIP code (handled through our secure payment partners)</li>
            <li>Support queries and communication</li>
          </ul>

          <h3 className="text-xl font-medium mt-4 mb-2">1.2 Automatically Collected Information</h3>
          <ul className="list-disc ml-6 space-y-1">
            <li>IP address, device type, browser, and operating system</li>
            <li>Pages viewed, clicks, session duration, and interactions</li>
            <li>Approximate location based on IP</li>
            <li>Cookies used for login, analytics, and personalization</li>
          </ul>

          <h3 className="text-xl font-medium mt-4 mb-2">1.3 Information from Third Parties</h3>
          <p>
            If you connect Smarty with third-party tools or platforms, we may receive information based on your
            permissions and settings.
          </p>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">2. How We Use Your Information</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>To operate and provide our services</li>
            <li>For secure login and account management</li>
            <li>To personalize your experience</li>
            <li>To process payments and manage subscriptions</li>
            <li>To improve features and fix issues</li>
            <li>To send updates, notifications, and alerts</li>
            <li>To prevent fraud, misuse, or security risks</li>
          </ul>
          <p className="mt-3 font-medium">
            <strong>Note:</strong> We do <u>not</u> use your personal data to train AI models.
          </p>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">3. How We Share Your Information</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Service providers (hosting, payments, analytics, support tools)</li>
            <li>Business partners needed for certain features</li>
            <li>Legal authorities when required</li>
            <li>During company mergers or restructuring</li>
            <li>With your explicit permission</li>
          </ul>
          <p className="mt-3">We <strong>never sell</strong> your personal information.</p>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">4. Cookies & Tracking</h2>
          <p>
            We use cookies to keep you logged in, remember preferences, analyze usage, and enhance your experience.
            You can control cookies through your browser settings, but disabling them may affect functionality.
          </p>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">5. Security</h2>
          <p>
            We use industry-standard encryption, secure storage, and protective measures. However, no system is
            100% secure. Please keep your password safe and contact us immediately if you suspect unauthorized activity.
          </p>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">6. Your Rights</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Access your data</li>
            <li>Update or correct your information</li>
            <li>Request account deletion</li>
            <li>Opt out of marketing</li>
          </ul>
          <p className="mt-2">
            To exercise any rights, contact us at{" "}
            <strong>support@accennovate.com</strong>.
          </p>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">7. Data Retention</h2>
          <p>
            We keep your data only as long as necessary for providing services, legal compliance, or security
            purposes. Deleted accounts remove most stored data unless legally required.
          </p>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">8. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Major changes will be notified via email or inside
            Smarty.
          </p>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="text-2xl font-semibold mb-3">9. Contact Us</h2>
          <p>
            For privacy concerns or questions, please contact us at:
            <br />
            <strong>Accennovate Tech Solutions</strong>
            <br />
            Email: <strong>support@accennovate.com</strong>
          </p>
        </section>
      </div>
    </div>
  );
}
